import { useEffect, useMemo, useState } from 'react';
import {
    OptionStyles,
    ProductOption,
    ProductOptionChangedEvent,
    ProductOptionModel,
    ProductOptionsTranslations,
} from '@vp/product-options-ui';
import {
    Box,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    Row,
    Spinner,
    Typography,
    useScreenClass,
} from '@vp/swan';
import { bugtracker } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { useBriefContext } from '../../../../BriefContext';
import { useBriefFormContext } from '../../../../BriefContext/BriefFormContext';
import { LoaderOverlay } from '../../../../LoaderOverlay';
import { type Field_ClientConfiguredField_Fragment } from '../../../brief.generated';
import { useTrackFieldChange } from '../../../useTrackFieldChange';
import { ClientConfiguredFieldProps } from '../types';
import { useProductOptions } from './ProductOptionsProvider';
import { findProductOptions } from './index';
import { AvailableOptionsType, ProductOptionReviewType, isProductOptionType } from './types';
import useUpdateProductOptionsDropdown from './useUpdateProductOptionsDropdown';

const MAX_OPTION_VALUES_FOR_BUTTON_SKIN = 4;

export function Input({ id, clientConfiguredValue }: ClientConfiguredFieldProps) {
    const { product } = useBriefContext();
    const { locale } = usePage();

    const {
        availableOptions,
        configuratorLoading,
        configuratorError,
        setSelectedOptions,
        selectedOptions,
        isValid,
        touched,
        setProductOptionTouched,
    } = useProductOptions();

    useEffect(() => {
        if (
            selectedOptions['Size'] !== 'Custom' &&
            (selectedOptions['Custom Height'] || selectedOptions['Custom Width'])
        ) {
            setSelectedOptions((prev) => ({
                ...prev,
                'Custom Height': '',
                'Custom Width': '',
            }));
        }
    }, [selectedOptions, setSelectedOptions]);

    if (configuratorError || availableOptions.length === 0) {
        return null;
    }

    return (
        <ProductOptionsTranslations locale={locale}>
            <LoaderOverlay loading={configuratorLoading}>
                <GridContainer>
                    <Row>
                        {availableOptions.map((option) => (
                            <Column
                                span={checkIfCustomField(option.name) ? 6 : 12}
                                key={option.name}
                            >
                                <ProductOptionInput
                                    key={option.name}
                                    option={option}
                                    questionId={id}
                                    clientConfiguredValue={clientConfiguredValue}
                                    selectedOptions={selectedOptions}
                                    setSelectedOptions={setSelectedOptions}
                                    isOptionErrored={!isValid(option.key)}
                                    setProductOptionTouched={setProductOptionTouched}
                                    touched={touched}
                                    locale={locale}
                                    currency={product?.pricing?.amount.currency ?? ''}
                                />
                            </Column>
                        ))}
                    </Row>
                </GridContainer>
            </LoaderOverlay>
        </ProductOptionsTranslations>
    );
}

const customFieldIds = ['Custom Height', 'Custom Width'];

function checkIfCustomField(name: string) {
    return customFieldIds.includes(name);
}

interface ProductOptionInputProps {
    option: AvailableOptionsType;
    questionId: string;
    clientConfiguredValue: string;
    selectedOptions: Record<string, string>;
    setSelectedOptions: (options: Record<string, string>) => void;
    isOptionErrored: boolean;
    touched: Record<string, boolean>;
    setProductOptionTouched: (optionKey?: string) => void;
    locale: string;
    currency: string;
}

function ProductOptionInput(props: ProductOptionInputProps) {
    const {
        option,
        questionId,
        clientConfiguredValue,
        selectedOptions,
        setSelectedOptions,
        isOptionErrored,
        touched,
        setProductOptionTouched,
        locale,
        currency,
    } = props;

    const screenClass = useScreenClass();

    const { briefId } = useBriefFormContext();
    const updateMutation = useUpdateProductOptionsDropdown(
        questionId,
        briefId,
        clientConfiguredValue
    );

    const trackFieldChange = useTrackFieldChange(`${questionId}.${option.key}`);

    if (selectedOptions['Size'] !== 'Custom' && checkIfCustomField(option.name)) {
        return null;
    }

    const handleChange = async (event: ProductOptionChangedEvent) => {
        const value = event.choiceKey || '';

        const newSelectedOptions = {
            ...selectedOptions,
            [option.key]: value,
        };

        setSelectedOptions(newSelectedOptions);

        setProductOptionTouched(option.key);

        await updateMutation(JSON.stringify(newSelectedOptions));
        trackFieldChange();
    };

    const style = getStyleForProductOption(option);

    return (
        <Box
            pb={style === 'buttons' ? 0 : 5}
            data-testid={`product-option-${option.key.toLowerCase()}`}
        >
            <ProductOption
                productOption={transformOptionToProductOption({
                    option,
                    selectedKey: selectedOptions[option.key],
                    style,
                    screenClass,
                })}
                tenant="vistaprint"
                currency={currency}
                fractionDigits={2}
                locale={locale}
                priceDisplayMode="none"
                showIncompatibleChoices={true}
                skin="standard"
                onChanged={handleChange}
                showFormErrors={touched[option.key] && isOptionErrored}
                uiDensityUpdate={true}
            />
        </Box>
    );
}

function transformOptionToProductOption({
    option,
    selectedKey,
    style,
    screenClass,
}: {
    option: AvailableOptionsType;
    selectedKey: string;
    style: OptionStyles;
    screenClass: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}): ProductOptionModel {
    // Note: isCompatible isn't available if switching over to the Selector API.

    return {
        key: option.key,
        name: option.name,
        style,
        choices: isProductOptionType(option)
            ? option.values
                  .filter((value) => {
                      if (option.key === 'Size' || option.key === 'Unfolded Size') {
                          return value.isCompatible;
                      }

                      return true;
                  })
                  .map((value) => {
                      const image =
                          screenClass !== 'xs' && value.imageUrl ? value.imageUrl : undefined;
                      return {
                          key: value.key,
                          name: value.name,
                          isCompatible: value.isCompatible,
                          image,
                      };
                  })
            : option.ranges,
        unitOfMeasure: isProductOptionType(option) ? undefined : option.unit,
        selectedKey,
    };
}

function getStyleForProductOption(option: AvailableOptionsType) {
    if (option.key === 'Size') {
        return 'dropdown';
    }

    if (!isProductOptionType(option)) return 'textbox';

    return option.values.length > MAX_OPTION_VALUES_FOR_BUTTON_SKIN ? 'dropdown' : 'buttons';
}

function useClientSelectedOptions(clientConfiguredValue: string) {
    return useMemo(() => {
        try {
            return JSON.parse(clientConfiguredValue);
        } catch (e) {
            const error = e as Error;
            bugtracker.notify(`Failed to parse client configured value: ${error.message}`);
            return {};
        }
    }, [clientConfiguredValue]);
}

export function ProductOptionsReview({
    clientConfiguredValue,
}: Field_ClientConfiguredField_Fragment) {
    const selectedOptions = useClientSelectedOptions(clientConfiguredValue);
    const { availableOptions, configuratorLoading, configuratorError } = useProductOptions();
    const [customerSelectedOptions, setCustomerSelectedOptions] = useState<
        ProductOptionReviewType[]
    >([]);

    useEffect(() => {
        const matchedProductOptions = availableOptions.filter((option) => {
            return Object.keys(selectedOptions).includes(option.key);
        });

        setCustomerSelectedOptions(
            findProductOptions(selectedOptions, matchedProductOptions, false)
        );
    }, [availableOptions, selectedOptions]);

    if (configuratorLoading) {
        return <Spinner accessibleText={__('Loading...')} />;
    }

    if (configuratorError || customerSelectedOptions.length === 0) {
        return null;
    }

    return (
        <Box>
            {customerSelectedOptions.map((option) => {
                return (
                    <Box key={`review-option-${option.key}`} mb={5}>
                        <Typography fontSkin="body-standard-bold" mb={4}>
                            {option.name}
                        </Typography>
                        <FlexBox alignItems="center">
                            {option.value.imageUrl && (
                                <FluidImage
                                    src={option.value.imageUrl}
                                    alt={option.value.name}
                                    mr={4}
                                    style={{
                                        width: 'auto',
                                        height: '48px',
                                        borderRadius: '4px',
                                    }}
                                />
                            )}
                            <Box>
                                <Typography
                                    fontSkin={
                                        option.value.imageUrl
                                            ? 'body-standard-bold'
                                            : 'body-standard'
                                    }
                                >
                                    {`${option.value.name} ${option.value.unit ?? ''}`}
                                </Typography>
                            </Box>
                        </FlexBox>
                    </Box>
                );
            })}
        </Box>
    );
}
